import { FilterOutlined, SearchOutlined } from '@ant-design/icons'
import { Badge, Button, Flex, Input, Popover, theme as antdTheme } from 'antd'
import type { ResolvedFilter } from 'features/libra/dashboard/ticketDashboard/filtering/applyFilters'
import type { CategorySelection } from 'features/libra/dashboard/ticketDashboard/types'
import type { ChangeEvent, FC, ReactNode } from 'react'
import { memo, useCallback } from 'react'
import styled from 'styled-components'

import { SelectFilter } from './SelectFilter'

/** Tailwind's base styles grow the input's line height; pinning it keeps it level with the select and button. */
const SearchInput = styled(Input)<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  align-items: center;
`

const FilterList = styled(Flex)`
  width: 16rem;
`

interface TicketFilterBarProps {
  filters: ResolvedFilter[]
  selection: CategorySelection
  query: string
  onQueryChange: (query: string) => void
  onCategoryChange: (category: string, values: ReadonlyArray<string>) => void
  leading?: ReactNode
}

const RawTicketFilterBar: FC<TicketFilterBarProps> = ({
  filters,
  selection,
  query,
  onQueryChange,
  onCategoryChange,
  leading,
}) => {
  const { token } = antdTheme.useToken()
  const handleQueryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onQueryChange(event.target.value),
    [onQueryChange]
  )
  const activeCount = Object.values(selection).filter(
    (values) => values?.length
  ).length

  return (
    <Flex gap='small' align='center'>
      {filters.length > 0 && (
        <Popover
          trigger='click'
          placement='bottomLeft'
          title='Filters'
          content={
            <FilterList vertical gap='small'>
              {filters.map((filter) => (
                <SelectFilter
                  key={filter.category}
                  filter={filter}
                  value={selection[filter.category]}
                  onChange={onCategoryChange}
                />
              ))}
            </FilterList>
          }>
          <Badge count={activeCount} size='small'>
            <Button icon={<FilterOutlined />} aria-label='Filters' />
          </Badge>
        </Popover>
      )}
      {leading}
      <SearchInput
        $height={token.controlHeight}
        allowClear
        placeholder='Search assets'
        prefix={<SearchOutlined />}
        value={query}
        onChange={handleQueryChange}
      />
    </Flex>
  )
}

export const TicketFilterBar = memo(RawTicketFilterBar)
